'use client'

import { AffiliateTrackable } from '@/components/server/analytics/affiliate-trackable'
import { useTranslation } from '@/hooks/client/use-translation'
import SvgArrowUpRightFromSquare from '@/icons/regular/ArrowUpRightFromSquare'
import type { DirectoryRelease } from '@/lib/directory/aggregate-releases'
import { formatPrice } from '@/lib/utils/format-price'

interface RowPriceProps {
  release: DirectoryRelease
}

// "From $X / pair" — and when the price comes from a seller we link, the price
// IS the offer link: an affiliate click with the site's usual tracking
// (sampled impression, click, seller and position), opening the seller in a
// new tab. A price with no link stays plain text. One dotted pattern spans the
// linked phrase so font-weight changes cannot restart the dot pattern.
// Keep the offer outside a paragraph to avoid the global article-link hover highlight.
export const RowPrice = ({ release }: RowPriceProps) => {
  const { t } = useTranslation()

  if (release.fromPrice == null) {
    return <p className="text-navy/50 text-[15px]">{t('directory.row.priceUnavailable', 'Price unavailable')}</p>
  }

  const amount = formatPrice(release.fromPrice)
  const priceText = `${t('price.from', 'From')} ${amount} ${t('price.perPair', '/ pair')}`
  const price = (
    <>
      <span className="text-navy/60 group-hover/price:text-navy">{t('price.from', 'From')} </span>
      <span className="font-semibold tabular-nums">{amount}</span>
      <span className="text-navy/60 group-hover/price:text-navy"> {t('price.perPair', '/ pair')}</span>
    </>
  )

  const offer = release.offer
  if (!offer) {
    return <p className="text-navy text-[15px]">{price}</p>
  }

  return (
    <div className="text-navy text-[15px]">
      <AffiliateTrackable
        seller={offer.sellerName}
        sellerType={offer.sellerType}
        release={release.slug}
        position="directory-price"
        cta="price"
        text={priceText}
        testName="directory"
        url={offer.href}
        impression
      >
        <a
          href={offer.href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          aria-label={t('directory.row.offerAria', '{{price}} at {{seller}} (opens in a new tab)', {
            price: priceText,
            seller: offer.sellerName,
          })}
          className="group/price focus-visible:outline-lapis hover:text-navy focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <span className="relative inline-block after:pointer-events-none after:absolute after:inset-x-0 after:bottom-px after:h-[1.25px] after:bg-[linear-gradient(to_right,currentColor_50%,transparent_50%)] after:bg-size-[2px_100%] after:opacity-30 group-hover/price:after:opacity-100">
            {price}
          </span>
          <SvgArrowUpRightFromSquare
            aria-hidden
            className="fill-navy/50 group-hover/price:fill-navy ml-1.5 inline-block h-3 w-3 align-[-1px]"
          />
        </a>
      </AffiliateTrackable>
    </div>
  )
}
